import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  NavigateToDataManagement,
  NavigateToGroupDetails,
  NavigateToMemberProfile,
  NavigateToProjectDetail,
  NavigateToProjects,
  NavigateToSettingsPage,
  NavigateToSnapshot,
  NavigateToTeamDetails,
  NavigateWithPreservedParams,
  SetUrlParamProps,
  UseAppNavigation,
} from "@hooks/navigation/use-app-navigation-types";
import {
  AccountSettingsTabs,
  ProjectTabs,
  QueryParams,
  TeamTabs,
} from "@router/route-params";
import {
  generateAccountSettingsRoute,
  generateCreditsRoute,
  generateGroupDetailsRoute,
  generateGroupsRoute,
  generateMemberProfileRoute,
  generateProjectDetailsRoute,
  generateProjectsRoute,
  generateSettingRoute,
  generateSubscriptionExpiredRoute,
  generateTeamDetailsRoute,
  getPreservedQueryParams,
} from "@router/router-helper";
import {
  getDataManagementUrl,
  OPEN_PROJECT_TARGET_ATTRIBUTE,
} from "@utils/project-utils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/** A hook that gathers all the navigation and routing in the app */
export function useAppNavigation(): UseAppNavigation {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  /** Navigates to a new path while preserving the query parameters from the current URL */
  function navigateWithPreservedParams({
    path,
    state = "in-app-navigation",
    shouldReplace = false,
  }: NavigateWithPreservedParams): void {
    const preservedSearch = getPreservedQueryParams(location.search);
    const cleanedPath = path.split("?")[0];
    navigate(
      {
        pathname: cleanedPath,
        search: preservedSearch,
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- React Router's NavigateOptions API
      { state, replace: shouldReplace }
    );
  }

  function navigateToRoot(): void {
    navigateWithPreservedParams({ path: "/" });
  }

  function navigateToProjects({
    companyId,
    projectStatus,
  }: NavigateToProjects): void {
    const path = generateProjectsRoute(companyId, projectStatus);
    navigateWithPreservedParams({ path });
  }

  function navigateToProjectDetail({
    companyId,
    projectId,
  }: NavigateToProjectDetail): void {
    const path = generateProjectDetailsRoute(companyId, projectId);
    navigateWithPreservedParams({ path });
  }

  function navigateToSnapshot({
    companyId,
    parentProjectId,
    snapshotId,
  }: NavigateToSnapshot): void {
    const path = generateProjectDetailsRoute(
      companyId,
      parentProjectId,
      ProjectTabs.snapshots
    );
    const preservedSearch = new URLSearchParams(
      getPreservedQueryParams(location.search)
    );
    preservedSearch.set(QueryParams.snapshotId, snapshotId);

    navigate(
      { pathname: path, search: preservedSearch.toString() },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- React Router's NavigateOptions API
      { replace: true }
    );
  }

  function navigateToGroups({ companyId }: BaseCompanyIdProps): void {
    const path = generateGroupsRoute(companyId);
    navigateWithPreservedParams({ path });
  }

  function navigateToGroupDetails({
    companyId,
    groupId,
  }: NavigateToGroupDetails): void {
    const path = generateGroupDetailsRoute(companyId, groupId);
    navigateWithPreservedParams({ path });
  }

  function navigateToMemberProfile({
    companyId,
    memberId,
  }: NavigateToMemberProfile): void {
    const path = generateMemberProfileRoute(companyId, memberId);
    navigateWithPreservedParams({ path });
  }

  function navigateToTeamDetails({
    companyId,
    teamId,
  }: NavigateToTeamDetails): void {
    const path = generateTeamDetailsRoute(companyId, teamId);
    navigateWithPreservedParams({ path });
  }

  function navigateToTeamMembers({
    companyId,
    teamId,
  }: NavigateToTeamDetails): void {
    const path = generateTeamDetailsRoute(companyId, teamId, TeamTabs.members);
    navigateWithPreservedParams({ path });
  }

  function navigateToDataManagement({
    projectId,
    shouldEnableDev,
    shouldEnableAlpha,
    shouldEnableBeta,
    shouldShowUpload,
    shouldShowPublish,
  }: NavigateToDataManagement): void {
    const path = getDataManagementUrl({
      projectId,
      shouldEnableDev,
      shouldEnableAlpha,
      shouldEnableBeta,
      shouldShowUpload,
      shouldShowPublish,
    }).href;
    window.open(path, OPEN_PROJECT_TARGET_ATTRIBUTE);
  }

  function navigateToSettingsPage({
    companyId,
    settingsTab,
  }: NavigateToSettingsPage): void {
    const path = generateSettingRoute(companyId, settingsTab);
    navigateWithPreservedParams({ path });
  }

  function navigateToAccountSettingsPage(
    accountSettingsTab: AccountSettingsTabs
  ): void {
    const path = generateAccountSettingsRoute(accountSettingsTab);
    navigateWithPreservedParams({
      path,
      state: "in-app-navigation",
    });
  }

  function navigateToCreditsPage({ companyId }: BaseCompanyIdProps): void {
    const path = generateCreditsRoute(companyId);
    navigateWithPreservedParams({
      path,
      state: "in-app-navigation",
    });
  }

  /** Navigates to the subscription expired page */
  function navigateToSubscriptionExpiredPage({
    companyId,
  }: BaseCompanyIdProps): void {
    const path = generateSubscriptionExpiredRoute(companyId);
    navigateWithPreservedParams({ path });
  }

  /** Sets a query parameter in the URL */
  function setUrlParam({ key, value }: SetUrlParamProps): void {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (value === null) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, value);
    }
    setSearchParams(newSearchParams);
  }

  /**
   * Returns the value of a query parameter from the URL
   */
  function getQueryParam(key: QueryParams): string | null {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(key);
  }

  return {
    navigateToRoot,
    navigateToProjects,
    navigateToProjectDetail,
    navigateToSnapshot,
    navigateToGroups,
    navigateToGroupDetails,
    navigateToMemberProfile,
    navigateToTeamDetails,
    navigateToTeamMembers,
    navigateToAccountSettingsPage,
    navigateToDataManagement,
    navigateToSettingsPage,
    navigateToCreditsPage,
    navigateToSubscriptionExpiredPage,
    setUrlParam,
    getQueryParam,
  };
}
